import CheckboxField from "@features/onboarding/components/CheckboxField";
import { Field, Label, Switch } from "@headlessui/react";
import Container from "@shared/components/Container";
import clsx from "clsx";

export type DesiredGenderValue = "female" | "male" | "nonbinary";

const INDIVIDUAL_GENDER_OPTIONS: readonly { value: DesiredGenderValue; label: string }[] = [
  { value: "female", label: "Women" },
  { value: "male", label: "Men" },
  { value: "nonbinary", label: "Other" },
];

const ALL_GENDERS: DesiredGenderValue[] = ["female", "male", "nonbinary"];

export interface DesiredGendersFieldProps {
  /** Current selected values */
  value: DesiredGenderValue[];
  /** Called when selection changes */
  onChange: (value: DesiredGenderValue[]) => void;
  /** Validation error message */
  error?: string | undefined;
  /** Name for the checkbox inputs (default: "desired_genders") */
  name?: string;
  /** Optional section label above the toggle (e.g. "Interested In") */
  label?: string;
}

/**
 * Shared desired-genders field: Everyone toggle + Women, Men, Non-binary checkboxes.
 * Used by onboarding DesiredGendersForm and profile ProfileEditForm.
 */
export default function DesiredGendersField({
  value,
  onChange,
  error,
  name = "desired_genders",
  label,
}: DesiredGendersFieldProps) {
  const selected = value ?? [];
  const isEveryoneSelected = INDIVIDUAL_GENDER_OPTIONS.every((opt) => selected.includes(opt.value));

  const handleCheckboxChange = (optionValue: DesiredGenderValue, checked: boolean) => {
    if (checked) {
      onChange([...selected, optionValue]);
    } else {
      onChange(selected.filter((v) => v !== optionValue));
    }
  };

  const handleEveryoneToggle = (checked: boolean) => {
    onChange(checked ? ALL_GENDERS : []);
  };

  return (
    <div>
      {label && <span className="block text-sm font-medium text-gray-700 mb-2">{label}</span>}
      <Field className="flex min-w-0 flex-wrap items-center gap-2" data-testid="desired-genders-everyone-row">
        <Switch
          checked={isEveryoneSelected}
          onChange={handleEveryoneToggle}
          className={clsx(
            "relative inline-flex h-8 w-20 shrink-0 items-center rounded-lg transition-colors",
            "focus:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2",
            isEveryoneSelected ? "bg-pink-600" : "bg-lavender-900"
          )}
          data-testid="everyone-toggle"
        >
          <span
            className={clsx(
              "inline-block h-6 w-6 rounded-md bg-white transform transition-transform",
              isEveryoneSelected ? "translate-x-13" : "translate-x-1"
            )}
          />
        </Switch>
        <Label className="min-w-0 flex-1 break-words text-left text-md font-medium text-black">
          I'm open to Everyone
        </Label>
      </Field>
      <div className="space-y-2 mt-4">
        {INDIVIDUAL_GENDER_OPTIONS.map((option) => (
          <Container key={option.value}>
            <CheckboxField
              name={name}
              value={option.value}
              label={option.label}
              formValue={selected}
              onChange={(e) => handleCheckboxChange(option.value, e.target.checked)}
              {...(error && { error })}
            />
          </Container>
        ))}
      </div>
      {error && (
        <p className="mt-1 text-sm text-red-600" data-testid="field-error">
          {error}
        </p>
      )}
    </div>
  );
}
